// src/services/api.ts
import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { getEnvironmentConfig } from './environment';
import { showAlert } from '../contexts/DialogContext';
import { showBusy, hideBusy } from '../contexts/BusyContext';
import { TENANT_BLOCKED_REASONS, type EntitlementForbiddenBody } from '../types/entitlement';
import { requestTermsAcceptance, markTermsAccepted } from '../contexts/TermsContext';
import { TERMOS_PENDENTE_REASON, type TermosPendenteBody } from '../types/termos';
import { requestSenhaChange } from '../contexts/SenhaObrigatoriaContext';
import { SENHA_PENDENTE_REASON, type SenhaPendenteBody, type SenhaMotivo } from '../types/senha';
import { logger } from '@/utils/logger';

// Augmentação do axios: flag opt-in `blocking` (Chunk 3 — plano Busy Overlay).
// Callsites conhecidos lentos marcam o request; o interceptor aciona o overlay.
declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Opt-in: exibe o Busy Overlay bloqueante enquanto o request estiver em voo. */
    blocking?: boolean;
    /**
     * Opt-out do safety net do Busy Overlay (widgets de fundo).
     *
     * O safety net abre o overlay BLOQUEANTE para qualquer request que exceda
     * `SAFETY_THRESHOLD_MS`. Isso é correto para uma ação do usuário, mas errado
     * para uma leitura de fundo que ele não pediu: a Home dispara 3 contagens no
     * load e um backend lento travaria a tela inteira num overlay que o usuário
     * não sabe de onde veio.
     *
     * Só usar em leitura secundária, cuja demora o usuário pode ignorar sem
     * prejuízo. Ignorado quando `blocking: true` (opt-in explícito vence).
     */
    silent?: boolean;
  }
}

const { BASE_URL_BACKEND } = getEnvironmentConfig();

// Cria uma instância do Axios com configuração padrão
const api: AxiosInstance = axios.create({
  baseURL: BASE_URL_BACKEND,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Controles para evitar múltiplos alerts
let is401Redirecting = false;
let isNetworkErrorShown = false;
// Guards do tratamento de 403 de entitlement (não resetam: o redirect dispara
// hard reload, que recria o módulo — mesmo padrão de is401Redirecting).
let is403ModuleRedirecting = false;
let isTenantBlockedRedirecting = false;

// --- Busy Overlay (Chunk 3) -------------------------------------------------
// Estratégia híbrida (C): opt-in explícito via `blocking: true` + safety net que
// dispara o overlay para qualquer request que exceda o threshold sem a flag.
const SAFETY_THRESHOLD_MS = 2000;
const SAFETY_NET_MESSAGE = 'Processando sua solicitação...';

// Estado por-request mantido fora do objeto config (sem poluir o tipo público
// nem mutar o config). A identidade do config é estável entre os interceptors.
const busyState = new WeakMap<object, { timer?: ReturnType<typeof setTimeout>; active: boolean }>();

// Limpa o timer do safety net e fecha o overlay se este request o havia aberto.
const finalizeBusy = (config?: AxiosRequestConfig): void => {
  if (!config) return;
  const state = busyState.get(config);
  if (!state) return;
  if (state.timer) clearTimeout(state.timer);
  if (state.active) hideBusy();
  busyState.delete(config);
};

// Request: abre o overlay imediatamente (opt-in), agenda o safety net, ou não
// faz nenhum dos dois quando o callsite marcou `silent` (leitura de fundo).
api.interceptors.request.use((config) => {
  if (config.blocking) {
    showBusy();
    busyState.set(config, { active: true });
  } else if (!config.silent) {
    const timer = setTimeout(() => {
      showBusy(SAFETY_NET_MESSAGE);
      const state = busyState.get(config);
      if (state) state.active = true;
    }, SAFETY_THRESHOLD_MS);
    busyState.set(config, { timer, active: false });
  }
  return config;
});

// Response do Busy Overlay — registrado ANTES do interceptor de erros (401/rede)
// para que o overlay feche antes de qualquer showAlert. Aquele permanece intocado.
api.interceptors.response.use(
  (response: AxiosResponse) => {
    finalizeBusy(response.config);
    return response;
  },
  (error: AxiosError) => {
    finalizeBusy(error.config);
    return Promise.reject(error);
  }
);
// --- fim Busy Overlay -------------------------------------------------------

// URLs isentas do gate de termos (backend não as bloqueia) OU de autenticação —
// um 2xx nelas NÃO implica que os termos vigentes foram aceitos (login retorna
// 200 mesmo com termos pendentes — issue #6 §1).
const isTermsGateExemptUrl = (url?: string): boolean => {
  if (!url) return true;
  return (
    url === '/login' ||
    url.includes('/v1/auth/microsoft-login') ||
    url.includes('/termos-vigente') ||
    url.includes('/aceitar-termos') ||
    url.includes('/logout')
  );
};

// URLs isentas do gate de SENHA (issue #27). `minha-senha` PRECISA ser isenta
// para não recursar no próprio gate (é a rota que troca a senha); as demais
// espelham as isentas de termos.
const isSenhaGateExemptUrl = (url?: string): boolean => {
  if (!url) return true;
  return (
    url === '/login' ||
    url.includes('/v1/auth/microsoft-login') ||
    url.includes('/minha-senha') ||
    url.includes('/termos-vigente') ||
    url.includes('/aceitar-termos') ||
    url.includes('/logout')
  );
};

// Interceptor de resposta para tratar erros globalmente
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Um 2xx numa rota PROTEGIDA implica que os termos vigentes já foram aceitos
    // (o backend só libera após o aceite) — habilita o item "Termos de Uso" no
    // dropdown. Rotas isentas/auth não contam.
    if (!isTermsGateExemptUrl(response.config?.url)) {
      markTermsAccepted();
    }
    return response;
  },
  async (error: AxiosError) => {
    logger.error('Erro na requisição:', error.config?.url, error.response?.status);
    // Ignorar tratamento de 401 para rotas de autenticação tratadas nas telas
    if (error.config?.url === '/login' || error.config?.url === '/v1/auth/microsoft-login') {
      return Promise.reject(error); // Deixa o Login.tsx tratar o erro
    }

    // Tratamento de erro 401 (sessão expirada) para outras rotas
    if (error.response?.status === 401 && !is401Redirecting) {
      is401Redirecting = true;
      const errorMessage = (error.response?.data as { message?: string })?.message || 'Sessão expirada. Realize novo login.';
      await showAlert(errorMessage, { variant: 'error', title: 'Sessão expirada' });
      sessionStorage.removeItem('jwtToken');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Tratamento de 403 de ENTITLEMENT (gate de módulo / status de tenant).
    // Cirúrgico: só age quando o corpo traz um `reason` conhecido; qualquer
    // outro 403 (ex.: "sem permissão" tratado localmente por componentes como
    // ApproverParamsSection) é repropagado intacto via fall-through.
    if (error.response?.status === 403) {
      const reason = (error.response?.data as EntitlementForbiddenBody | undefined)?.reason ?? '';
      // Termos de uso pendentes (issue #6): abre o modal de aceite e, no aceite,
      // repete a requisição originalmente bloqueada — sem re-login (§5). O
      // single-flight em requestTermsAcceptance garante um único modal quando
      // várias requests tomam 403 simultaneamente. As próprias rotas de termos
      // são isentas (evita recursão se alguma delas 403-ar por engano).
      const termosConfig = error.config as
        | (InternalAxiosRequestConfig & { _termosRetried?: boolean })
        | undefined;
      if (reason === TERMOS_PENDENTE_REASON && !isTermsGateExemptUrl(termosConfig?.url)) {
        // Circuit breaker: se já retentamos após um aceite e ainda deu 403,
        // repropaga em vez de reabrir o modal em loop (backend em desacordo).
        if (termosConfig?._termosRetried) {
          return Promise.reject(error);
        }
        const versaoAtual =
          (error.response?.data as TermosPendenteBody | undefined)?.versao_atual ?? '';
        const accepted = await requestTermsAcceptance(versaoAtual);
        if (accepted && termosConfig) {
          markTermsAccepted();
          termosConfig._termosRetried = true;
          return api.request(termosConfig);
        }
        return Promise.reject(error);
      }
      // Troca de senha obrigatória pendente (issue #27): ramo IRMÃO do de termos,
      // colocado DEPOIS dele — o backend só emite `senha_pendente` após o aceite
      // dos termos (ordem termos→senha). Mesmo padrão: single-flight + circuit
      // breaker (`_senhaRetried`) + retry da request bloqueada. `minha-senha` é
      // isenta (evita recursão no próprio gate).
      const senhaConfig = error.config as
        | (InternalAxiosRequestConfig & { _senhaRetried?: boolean })
        | undefined;
      if (reason === SENHA_PENDENTE_REASON && !isSenhaGateExemptUrl(senhaConfig?.url)) {
        if (senhaConfig?._senhaRetried) {
          return Promise.reject(error);
        }
        const body = error.response?.data as SenhaPendenteBody | undefined;
        const motivo: SenhaMotivo = body?.motivo ?? 'primeiro_acesso';
        const padraoExigido = body?.padrao_exigido ?? '';
        const changed = await requestSenhaChange(motivo, padraoExigido);
        if (changed && senhaConfig) {
          senhaConfig._senhaRetried = true;
          return api.request(senhaConfig);
        }
        return Promise.reject(error);
      }
      if (reason.startsWith('modulo_nao_contratado')) {
        // Módulo não contratado: safety net da janela em que o contrato mudou e
        // a UI ainda não atualizou (o gate de UX deveria ter escondido a tela).
        // showAlert (bloqueante) antes do hard reload — um toast seria destruído
        // pelo reload antes de ser lido. Mesmo padrão do 401 (premissa #5/ADR-005).
        if (!is403ModuleRedirecting && window.location.pathname !== '/home') {
          is403ModuleRedirecting = true;
          await showAlert('Este módulo não está disponível no seu plano.', {
            variant: 'warning',
            title: 'Módulo indisponível',
          });
          window.location.href = '/home';
        }
        return Promise.reject(error);
      }
      if ((TENANT_BLOCKED_REASONS as readonly string[]).includes(reason)) {
        // Tenant inteiro bloqueado: trata como sessão inválida (acesso suspenso),
        // reusando o padrão defensivo window.location.href (premissa #5 / ADR-005).
        if (!isTenantBlockedRedirecting) {
          isTenantBlockedRedirecting = true;
          await showAlert('Seu acesso está suspenso. Entre em contato com o suporte.', {
            variant: 'error',
            title: 'Acesso suspenso',
          });
          sessionStorage.removeItem('jwtToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
      // 403 sem reason de entitlement: fall-through, o componente trata.
    }

    // Tratamento de erro de rede (backend offline)
    if (!error.response && !isNetworkErrorShown) {
      isNetworkErrorShown = true;
      await showAlert('Não foi possível conectar ao servidor. Verifique sua conexão ou tente novamente mais tarde.', { variant: 'error' });
      setTimeout(() => (isNetworkErrorShown = false), 1000); // Reseta após 1s para novas tentativas
    }

    return Promise.reject(error); // Repropaga o erro para o chamador
  }
);

// Funções de requisição exportadas
export const apiGet = (endpoint: string, config?: AxiosRequestConfig) =>
  api.get(endpoint, {
    ...config,
    headers: { Authorization: `Bearer ${sessionStorage.getItem('jwtToken') || ''}` },
  }).then((response) => response);

export const apiPost = (endpoint: string, body?: unknown, config?: AxiosRequestConfig) => {
  return api.post(endpoint, body, {
    ...config,
    headers: { Authorization: `Bearer ${sessionStorage.getItem('jwtToken') || ''}` },
  }).then((response) => response);
};

export const apiPut = (endpoint: string, body?: unknown) =>
  api.put(endpoint, body, {
    headers: { Authorization: `Bearer ${sessionStorage.getItem('jwtToken') || ''}` },
  }).then((response) => response);

export const apiDelete = (endpoint: string) => api.delete(endpoint, {
    headers: { Authorization: `Bearer ${sessionStorage.getItem('jwtToken') || ''}` },
}).then((response) => response);
